import { useMemo } from 'react';
import { useAuthStore } from '@/stores/auth';

export type MenuAccessRoute = {
  access?: string | string[];
  routes?: MenuAccessRoute[];
  [key: string]: unknown;
};

function canAccess(access: string | string[] | undefined, permissions: string[]) {
  if (!access) return true;
  if (Array.isArray(access)) return access.some((item) => permissions.includes(item));
  return permissions.includes(access);
}

function filterRoute(route: MenuAccessRoute, permissions: string[]): MenuAccessRoute | null {
  if (!canAccess(route.access, permissions)) return null;

  if (!route.routes) return route;

  const routes = route.routes
    .map((child) => filterRoute(child, permissions))
    .filter((child): child is MenuAccessRoute => child !== null);

  if (route.routes.length > 0 && routes.length === 0) return null;

  return { ...route, routes };
}

export function useMenuAccess<T extends MenuAccessRoute>(routes: T): T {
  const user = useAuthStore((state) => state.user);
  const permissions = user?.permissions ?? [];

  return useMemo(() => {
    const filtered = filterRoute(routes, permissions);
    return (filtered ?? { ...routes, routes: [] }) as T;
  }, [permissions, routes]);
}
